import type { MessageDTO } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { savedApi } from '@/api/endpoints';
import { useSaved } from '@/stores/saved';
import { toast } from '@/stores/ui';

const reminderFmt = new Intl.DateTimeFormat(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
const reminderLongFmt = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

/** "Fri 09:00" this week, "12 Oct, 09:00" further away. */
export function formatReminder(iso: string, now = Date.now()): string {
  const at = new Date(iso);
  return at.getTime() - now < 6 * 24 * 60 * 60_000 ? reminderFmt.format(at) : reminderLongFmt.format(at);
}

/** Saves a message, or takes it out of your saved messages. */
export async function toggleSaved(message: Pick<MessageDTO, 'id'>) {
  const saved = message.id in useSaved.getState().byId;
  try {
    if (saved) {
      await savedApi.unsave(message.id);
      useSaved.getState().apply({ messageId: message.id, saved: false, remindAt: null });
      toast.success('Removed from your saved messages.');
    } else {
      const result = await savedApi.save(message.id);
      useSaved.getState().apply(result);
      toast.success('Saved. Find it under the bookmark at the top of the screen.');
    }
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

/** Saves a message with a reminder at `at` (null clears the reminder but keeps it saved). */
export async function setReminder(message: Pick<MessageDTO, 'id'>, at: Date | null): Promise<boolean> {
  try {
    const result = await savedApi.save(message.id, at?.toISOString() ?? null);
    useSaved.getState().apply(result);
    toast.success(at ? `Reminder set for ${formatReminder(at.toISOString())}.` : 'Reminder removed.');
    return true;
  } catch (err) {
    toast.error(errorMessage(err));
    return false;
  }
}
